import {
  Table,
  Tbody,
  Td,
  Text,
  Tooltip,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { Envs } from '@ae/envs';
import { InfoIcon } from '@ae/icons';
import {
  useApiEndpoint,
  useAppBasePath,
  useAssetsPath,
  useHostname,
  useLegacyAuthEndpoint,
  useLegacyLogoutEndpoint,
  useLinkLegacyBehavior,
  useLocale,
  usePathname,
  useQuery,
  useRequestUrl,
} from '@ae/shared';
import { UIButton, UIPopover } from '@ae/shared-ui';
import { EnvironmentTag } from './EnvironmentTag';
import { Environment, getEnvironmentFromHostname } from './utils';

export const DebugPopover = () => {
  const requestUrl = useRequestUrl();
  const hostname = useHostname();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const appBasePath = useAppBasePath();
  const apiEndpoint = useApiEndpoint();
  const legacyAuthEndpoint = useLegacyAuthEndpoint();
  const legacyLogoutEndpoint = useLegacyLogoutEndpoint();
  const locale = useLocale();
  const assetsPath = useAssetsPath();
  const pathname = usePathname();
  const linkLegacyBehavior = useLinkLegacyBehavior();
  const query = useQuery();

  if (Envs.hideDevTools) {
    return null;
  }

  const data = [
    { label: 'App base path', value: appBasePath || 'Empty' },
    { label: 'API endpoint', value: apiEndpoint || 'Empty' },
    {
      label: 'Auth endpoint',
      value: legacyAuthEndpoint(requestUrl, locale) || 'Empty',
    },
    {
      label: 'Logout endpoint',
      value: legacyLogoutEndpoint(requestUrl) || 'Empty',
    },
    { label: 'Locale', value: locale || 'Empty' },
    { label: 'Assets path', value: assetsPath || 'Empty' },
    { label: 'Pathname', value: pathname || 'Empty' },
    {
      label: 'Link legacy behavior',
      value: linkLegacyBehavior ? 'true' : 'false',
    },
    {
      label: 'Request URL',
      value: requestUrl || 'Empty',
    },
    {
      label: 'Query',
      value: query || 'Empty',
    },
    {
      label: 'Environment',
      value: <EnvironmentTag />,
    },
  ].sort((a, b) => a.label.localeCompare(b.label));

  const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Tr>
      <Td py={3}>
        <Text color="black" fontSize="sm" fontWeight="bold">
          {label}
        </Text>
      </Td>
      <Td py={3}>
        {typeof value === 'string' ? (
          <Tooltip label={value}>
            <Text
              color="black"
              fontSize="sm"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              maxW="300px"
              title={value}
            >
              {value}
            </Text>
          </Tooltip>
        ) : (
          value
        )}
      </Td>
    </Tr>
  );

  return (
    <UIPopover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      title="Debug Information"
      triggerComponent={
        <UIButton
          variant="primary"
          position="fixed"
          h={12}
          w={12}
          borderRadius="full"
          bottom={
            getEnvironmentFromHostname(hostname) === Environment.Local
              ? '66px'
              : '12px'
          }
          right="12px"
          zIndex={2000}
          onClick={onOpen}
        >
          <InfoIcon size="22px" />
        </UIButton>
      }
    >
      <Table size="sm">
        <Tbody>
          {data.map((item) => (
            <Row
              key={item.label}
              label={item.label}
              value={item.value as any}
            />
          ))}
        </Tbody>
      </Table>
    </UIPopover>
  );
};
